import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { CalendarDays, ArrowLeft, ArrowRight, Home as HomeIcon, Clock, CheckCircle2, Loader2, AlertCircle, Send, User } from "lucide-react";

const fmtDay = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const wd = new Intl.DateTimeFormat("sv-SE", { weekday: "short" }).format(d);
  const dm = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short" }).format(d);
  return { wd: wd.charAt(0).toUpperCase() + wd.slice(1), dm };
};
const fmtLong = (iso) =>
  new Intl.DateTimeFormat("sv-SE", { weekday: "long", day: "numeric", month: "long" }).format(new Date(iso + "T00:00:00"));

export default function Booking() {
  const [services, setServices] = useState([]);
  const [dates, setDates] = useState([]);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  useEffect(() => {
    api.get("/booking/services").then((res) => {
      setServices(res.data.services);
      const list = [];
      const start = new Date(res.data.min_date + "T00:00:00");
      for (let i = 0; i <= 30; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        list.push(d.toISOString().slice(0, 10));
      }
      setDates(list);
    });
  }, []);

  const pickDate = async (iso) => {
    setDate(iso);
    setSlot(null);
    setSlotsLoading(true);
    try {
      const res = await api.get(`/booking/availability?date=${iso}`);
      setSlots(res.data.slots);
    } finally {
      setSlotsLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) { setError("Fyll i namn och telefonnummer."); return; }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/booking", { service: service.id, date, start: slot.start, ...form });
      setDone(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Något gick fel. Försök igen.";
      setError(msg);
      if (err?.response?.status === 409) { await pickDate(date); setSlot(null); }
    } finally {
      setSubmitting(false);
    }
  };

  const stepNum = done ? 4 : slot ? 3 : service ? 2 : 1;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Link to="/" data-testid="booking-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <CalendarDays size={46} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Boka en tid
      </h1>
      <p className="text-lg md:text-xl leading-relaxed text-[#4A4A4A] mt-4">
        Välj tjänst, en ledig tid som passar dig och fyll i dina uppgifter. Varje pass är 1 timme.
      </p>

      {/* Progress */}
      {!done && (
        <div className="flex items-center gap-2 mt-6" data-testid="booking-steps">
          {["Tjänst", "Tid", "Uppgifter"].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <span className={`flex items-center justify-center w-8 h-8 rounded-full text-base font-bold ${stepNum > i + 1 ? "bg-[#0B7A3F] text-white" : stepNum === i + 1 ? "bg-[#0056D2] text-white" : "bg-gray-200 text-[#4A4A4A]"}`}>{i + 1}</span>
              <span className="text-base font-semibold text-[#1A1A1A] hidden sm:inline">{label}</span>
              {i < 2 && <span className="w-6 h-0.5 bg-gray-200" />}
            </div>
          ))}
        </div>
      )}

      {done ? (
        <div data-testid="booking-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-8 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4"><CheckCircle2 size={56} strokeWidth={2} /></span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Din tid är bokad!</h2>
          <div className="text-lg text-[#1A1A1A] mt-4 space-y-1">
            <p><strong>{done.service_label}</strong></p>
            <p className="capitalize">{fmtLong(done.date)}</p>
            <p>kl {done.start}–{done.end}</p>
          </div>
          <p className="text-base text-[#4A4A4A] mt-4">Vi har skickat en bekräftelse till din e-post. Vi hör av oss om något behöver ändras.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <>
          {/* Step 1: service */}
          <section className="mt-8">
            <h2 className="font-heading text-2xl font-bold text-[#1A1A1A]">1. Välj tjänst</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {services.map((s) => (
                <button key={s.id} data-testid={`booking-service-${s.id}`}
                  onClick={() => { setService(s); setDate(null); setSlot(null); setSlots([]); }}
                  className={`text-left min-h-[60px] px-5 py-3 rounded-xl border-2 text-lg font-semibold transition-colors ${service?.id === s.id ? "border-[#0056D2] bg-blue-50 text-[#0056D2]" : "border-gray-300 text-[#1A1A1A] hover:border-[#0056D2]"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </section>

          {/* Step 2: date + slot */}
          {service && (
            <section className="mt-10" data-testid="booking-date-section">
              <h2 className="font-heading text-2xl font-bold text-[#1A1A1A]">2. Välj dag och tid</h2>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4">
                {dates.map((iso) => {
                  const { wd, dm } = fmtDay(iso);
                  const active = date === iso;
                  return (
                    <button key={iso} data-testid={`booking-date-${iso}`} onClick={() => pickDate(iso)}
                      className={`flex flex-col items-center justify-center min-h-[64px] rounded-lg border-2 transition-colors ${active ? "border-[#0056D2] bg-[#0056D2] text-white" : "border-gray-300 text-[#1A1A1A] hover:border-[#0056D2]"}`}>
                      <span className="text-sm font-semibold">{wd}</span>
                      <span className="text-base font-bold">{dm}</span>
                    </button>
                  );
                })}
              </div>

              {date && (
                <div className="mt-6">
                  <p className="text-lg font-semibold text-[#1A1A1A] capitalize">{fmtLong(date)}</p>
                  {slotsLoading ? (
                    <p className="flex items-center gap-2 text-[#4A4A4A] mt-3"><Loader2 className="animate-spin" size={22} /> Hämtar lediga tider…</p>
                  ) : slots.length === 0 ? (
                    <p className="text-[#4A4A4A] mt-3">Inga tider den här dagen.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                      {slots.map((sl) => (
                        <button key={sl.start} data-testid={`booking-slot-${sl.start}`} disabled={!sl.available}
                          onClick={() => setSlot(sl)}
                          className={`inline-flex items-center justify-center gap-2 min-h-[56px] rounded-lg border-2 text-lg font-semibold transition-colors ${
                            !sl.available ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed line-through"
                            : slot?.start === sl.start ? "border-[#0056D2] bg-[#0056D2] text-white"
                            : "border-gray-300 text-[#1A1A1A] hover:border-[#0056D2]"}`}>
                          <Clock size={18} /> {sl.start}–{sl.end}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Step 3: form */}
          {slot && (
            <section className="mt-10" data-testid="booking-form-section">
              <h2 className="font-heading text-2xl font-bold text-[#1A1A1A]">3. Dina uppgifter</h2>
              <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-4 mt-4 text-[#1A1A1A]">
                <p className="text-lg"><strong>{service.label}</strong></p>
                <p className="capitalize">{fmtLong(date)}, kl {slot.start}–{slot.end}</p>
              </div>
              <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-4 space-y-6">
                <div>
                  <label htmlFor="b-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
                  <input id="b-name" data-testid="booking-name" value={form.name} onChange={set("name")}
                    className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
                </div>
                <div>
                  <label htmlFor="b-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-post</label>
                  <input id="b-email" type="email" data-testid="booking-email" value={form.email} onChange={set("email")}
                    className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
                </div>
                <div>
                  <label htmlFor="b-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
                  <input id="b-phone" type="tel" data-testid="booking-phone" value={form.phone} onChange={set("phone")}
                    className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
                </div>

                {error && (
                  <p data-testid="booking-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                    <AlertCircle size={22} /> {error}
                  </p>
                )}

                <button data-testid="booking-submit" type="submit" disabled={submitting}
                  className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
                  {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
                  {submitting ? "Bokar…" : "Bekräfta bokning"}
                </button>

                <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
                  Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
                </p>
              </form>
            </section>
          )}
        </>
      )}
    </div>
  );
}
